import { LucideIcon, Image, Carousel } from '@skin-seoul/design-system';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { Box, useBreakpointValue, Flex } from '@skin-seoul/design-system';
import type { ReviewImage } from '..';

type CarouselApi = {
  scrollTo: (index: number) => void;
  canScrollNext: () => boolean;
  canScrollPrev: () => boolean;
  selectedScrollSnap: () => number;
  scrollSnapList: () => number[];
};

interface ReviewImageDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  image: ReviewImage | null;
  images?: ReviewImage[];
  initialIndex?: number;
}

const MotionBox = motion.create(Box);

const Overlay = ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
  <MotionBox
    position="fixed"
    top={0}
    left={0}
    right={0}
    bottom={0}
    backgroundColor="rgba(0, 0, 0, 0.70)"
    zIndex={999999}
    display="flex"
    alignItems="center"
    justifyContent="center"
    {...props}
  >
    {children}
  </MotionBox>
);

export const ReviewImageDetailModal = ({
  isOpen,
  onClose,
  image,
  images = [],
  initialIndex = 0,
}: ReviewImageDetailModalProps) => {
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();

  const displayImages = isMobile && images.length > 0 ? images : image ? [image] : [];
  const startIndex = isMobile && images.length > 0 ? initialIndex : 0;

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (carouselApi && isOpen && isMobile && images.length > 0) {
      carouselApi.scrollTo(startIndex);
    }
  }, [carouselApi, isOpen, isMobile, images.length, startIndex]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (!image && displayImages.length === 0) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleOverlayClick}
        >
          {isMobile && images.length > 1 ? (
            <Carousel
              opts={{
                align: 'center',
                loop: false,
              }}
              setApi={setCarouselApi}
            >
              <Carousel.Content
                w="100vw"
                onClickCapture={(e) => {
                  const target = e.target as HTMLElement;
                  const isImageClick = target.tagName === 'IMG' || target.closest('img');

                  if (!isImageClick) {
                    onClose();
                  }
                }}
              >
                {displayImages.map((img, index) => (
                  <Carousel.Item key={img.id || index}>
                    <Flex h="100vh" align="center" justify="center" onClick={handleContentClick}>
                      <Image
                        src={img.url}
                        alt={img.alt}
                        maxW="100vw"
                        maxH="100dvh"
                        objectFit="contain"
                        onClick={handleContentClick}
                      />
                    </Flex>
                  </Carousel.Item>
                ))}
              </Carousel.Content>
              <Carousel.Previous
                left="8px"
                w="48px"
                h="48px"
                borderRadius="50%"
                bg="rgba(247, 247, 249, 0.2)"
                backdropFilter="blur(8px)"
                border="none"
                _hover={{ bg: 'rgba(247, 247, 249, 0.4)' }}
                variant="ghost"
              >
                <LucideIcon name="ChevronLeft" w="36px" h="36px" color="neutral.50" />
              </Carousel.Previous>
              <Carousel.Next
                right="8px"
                w="48px"
                h="48px"
                borderRadius="50%"
                bg="rgba(247, 247, 249, 0.2)"
                backdropFilter="blur(8px)"
                border="none"
                _hover={{ bg: 'rgba(247, 247, 249, 0.4)' }}
                variant="ghost"
              >
                <LucideIcon name="ChevronRight" w="36px" h="36px" color="neutral.50" />
              </Carousel.Next>
            </Carousel>
          ) : (
            <Box
              maxW={isMobile ? '100vw' : '90vw'}
              maxH={isMobile ? '100dvh' : '90dvh'}
              w={isMobile ? '100vw' : '90vw'}
              h={isMobile ? '100dvh' : '90dvh'}
              display="flex"
              alignItems="center"
              justifyContent="center"
              p={isMobile ? 0 : 4}
              onClick={onClose}
            >
              <Image
                src={displayImages[0]?.url || image?.url}
                alt={displayImages[0]?.alt || image?.alt}
                maxW="100%"
                maxH="100%"
                w={isMobile ? '100vw' : '100%'}
                h="auto"
                objectFit="contain"
                borderRadius={isMobile ? '0' : '8px'}
              />
            </Box>
          )}
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default ReviewImageDetailModal;
